import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import FormGroup from '@mui/material/FormGroup';
import { useMutation } from '@tanstack/react-query';
import type { Chat } from '@trinity/chat';
import { useChatApiClient } from '../../containers/Chat/useApiClient';
import { CancelAction } from '../presentation/Message.QueryHint/CancelAction';
import { AnalysisQueryHint } from '../presentation/Message.QueryHint/QueryHint';
import { ActiveChatSessionContext } from './context';
import { SegmentationTag } from './Segmentation.Tag';
import { useCancelSegmentation } from './hooks/useCancelSegmentation';

type TagWithSelection = {
  id: number;
  name: string;
  occurrences: number;
  selected: boolean;
};

type Props = {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationTagsNeeded;
};

export const SegmentationTagSelection = (props: Props) => {
  const chat = useContext(ActiveChatSessionContext);
  const apiClient = useChatApiClient();

  // Initialize local state from props
  const [tags, setTags] = useState<TagWithSelection[]>(() =>
    (props.value.availableTags ?? []).map((tag) => ({
      ...tag,
      selected: props.value.selectedTags.includes(tag.id),
    }))
  );

  const tagSelectionMutation = useMutation({
    mutationFn: async (selectedTagIds: number[]) => {
      const response = await apiClient.authenticatedClient.post<{
        error: boolean;
        citations: Chat.Citation[];
        value: Chat.QueryResponse;
        state?: { segmentationState: Chat.QueryState.SegmentationQuestionsNeeded };
      }>('/chat/segmentation/tags-selected', {
        queryIdentifier: props.queryIdentifier,
        chatIdentifier: chat.identifier,
        selectedTags: selectedTagIds,
      });
      return response.data;
    },
  });

  const handleTagChange = useCallback((id: number, checked: boolean) => {
    setTags((prevTags) => {
      const selectedCount = prevTags.filter((t) => t.selected).length;

      // If trying to select (check) and already at max (3), prevent selection
      if (checked && selectedCount >= 3) {
        return prevTags;
      }

      return prevTags.map((tag) => (tag.id === id ? { ...tag, selected: checked } : tag));
    });
  }, []);

  const handleContinue = useCallback(() => {
    const selectedTagIds = tags.filter((t) => t.selected).map((t) => t.id);
    tagSelectionMutation.mutate(selectedTagIds);
  }, [tags, tagSelectionMutation]);

  const cancelMutation = useCancelSegmentation({ queryIdentifier: props.queryIdentifier });

  const handleCancel = useCallback(() => {
    return cancelMutation.mutateAsync();
  }, [cancelMutation]);

  const canContinue = useMemo(
    () => !tagSelectionMutation.isPending && tags.filter((t) => t.selected).length > 0,
    [tagSelectionMutation.isPending, tags]
  );

  const sortedTags = useMemo(() => [...tags].sort((a, b) => b.occurrences - a.occurrences), [tags]);

  const selectedCount = useMemo(() => tags.filter((t) => t.selected).length, [tags]);

  const isEmpty = sortedTags.length === 0;

  if (isEmpty) {
    return (
      <Root>
        <HeaderCopyEmpty>
          I couldn't find enough tags for this record, you must tag records before you can run an analysis.
        </HeaderCopyEmpty>
        <Actions>
          <CancelAction displayValue="I'll go tag some records." onClick={handleCancel} />
        </Actions>
      </Root>
    );
  }

  return (
    <Root>
      <HeaderCopy>Ok, great! Let's get started. What tags would you like to analyze?</HeaderCopy>
      <PickCopy>Pick up to three tags.</PickCopy>
      <Tags>
        <FormGroup>
          {sortedTags.map((tag) => (
            <SegmentationTag
              key={tag.id}
              id={tag.id}
              label={tag.name}
              checked={tag.selected}
              occurrences={tag.occurrences}
              onChange={handleTagChange}
              readonly={selectedCount >= 3 && !tag.selected}
            />
          ))}
        </FormGroup>
      </Tags>
      <Actions>
        <AnalysisQueryHint
          disabled={!canContinue}
          value="Let's analyze these tags."
          onClick={handleContinue}
        />
        <CancelAction displayValue="Actually, I'd like to do something else." onClick={handleCancel} />
      </Actions>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
`;

const HeaderCopy = styled.div`
  box-sizing: border-box;
  border-bottom: 1px solid ${(props) => props.theme.palette.gray.light1};
  padding-bottom: 10px;
  margin-bottom: 10px;
`;

const HeaderCopyEmpty = styled(HeaderCopy)`
  box-sizing: border-box;
  border-bottom: none;
  padding-bottom: 0;
  margin-bottom: 0;
`;

const PickCopy = styled.div`
  font-size: 15px;
  font-family: ${(props) => props.theme.fonts.semiBold};
  margin-bottom: 10px;
`;

const Tags = styled.div`
  display: flex;
  flex-direction: column;

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const Actions = styled.div`
  margin-top: 10px;

  > :not(:last-child) {
    margin-bottom: 10px;
  }
`;
